import React, { useState } from "react";
import "./sidebar-tab-panel.scss";

export interface SidebarTab {
  id: string;
  label: string;
  /** Any icon or node rendered at the right end of the tab. */
  icon?: React.ReactNode;
  disabled?: boolean;
}

export interface SidebarTabPanelProps {
  tabs: SidebarTab[];
  value?: string;
  defaultValue?: string;
  onChange?: (id: string) => void;
  /**
   * Border color for the sidebar rail and content pane.
   * Defaults to Primary Gray 14 (#D6D6D6).
   */
  borderColor?: string;
  /**
   * Left accent color for the selected tab.
   * Defaults to Primary Gray 3 (#2E2E2E).
   */
  activeBorderColor?: string;
  /**
   * Text color for unselected tabs.
   * Defaults to Primary Gray 8 (#7A7A7A).
   */
  textColor?: string;
  /**
   * Text color for the selected tab.
   * Defaults to Primary Gray 1 (#0F0F0F).
   */
  activeTextColor?: string;
  sidebarWidth?: string | number;
  sidebarTitle?: React.ReactNode;
  children?: React.ReactNode | ((activeTabId: string) => React.ReactNode);
  className?: string;
}

export const SidebarTabPanel: React.FC<SidebarTabPanelProps> = ({
  tabs,
  value,
  defaultValue,
  onChange,
  borderColor,
  activeBorderColor,
  textColor,
  activeTextColor,
  sidebarWidth,
  sidebarTitle,
  children,
  className = "",
}) => {
  const firstEnabledId = tabs.find((tab) => !tab.disabled)?.id;
  const [uncontrolledValue, setUncontrolledValue] = useState(
    defaultValue ?? firstEnabledId ?? ""
  );
  const isControlled = value !== undefined;
  const activeTabId = isControlled ? value : uncontrolledValue;

  const selectTab = (tab: SidebarTab) => {
    if (tab.disabled) return;
    if (!isControlled) {
      setUncontrolledValue(tab.id);
    }
    onChange?.(tab.id);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    tab: SidebarTab,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      selectTab(tab);
      return;
    }

    const enabledTabs = tabs
      .map((item, itemIndex) => ({ tab: item, index: itemIndex }))
      .filter((item) => !item.tab.disabled);

    const currentPos = enabledTabs.findIndex((item) => item.index === index);
    let nextPos = -1;

    if (event.key === "ArrowDown") {
      nextPos = (currentPos + 1) % enabledTabs.length;
    } else if (event.key === "ArrowUp") {
      nextPos = (currentPos - 1 + enabledTabs.length) % enabledTabs.length;
    } else if (event.key === "Home") {
      nextPos = 0;
    } else if (event.key === "End") {
      nextPos = enabledTabs.length - 1;
    }

    if (nextPos < 0) return;

    const nextTab = enabledTabs[nextPos];
    if (!nextTab) return;

    event.preventDefault();
    const el = (event.currentTarget as HTMLElement).parentElement?.children[
      nextTab.index
    ] as HTMLElement | undefined;
    el?.focus();
    selectTab(nextTab.tab);
  };

  const containerStyle: React.CSSProperties = {
    ...(borderColor
      ? { "--sidebar-tab-border-color": borderColor }
      : {}),
    ...(activeBorderColor
      ? { "--sidebar-tab-active-border-color": activeBorderColor }
      : {}),
    ...(textColor ? { "--sidebar-tab-text-color": textColor } : {}),
    ...(activeTextColor
      ? { "--sidebar-tab-active-text-color": activeTextColor }
      : {}),
    ...(sidebarWidth
      ? {
          "--sidebar-tab-sidebar-width":
            typeof sidebarWidth === "number"
              ? `${sidebarWidth}px`
              : sidebarWidth,
        }
      : {}),
  } as React.CSSProperties;

  const content =
    typeof children === "function" ? children(activeTabId) : children;

  return (
    <div
      className={`sidebar-tab-panel ${className}`.trim()}
      style={containerStyle}
    >
      <div className="sidebar-tab-panel__sidebar">
        {sidebarTitle && (
          <div className="sidebar-tab-panel__title">{sidebarTitle}</div>
        )}
        <div className="sidebar-tab-panel__tabs" role="tablist" aria-orientation="vertical">
          {tabs.map((tab, index) => {
            const isActive = activeTabId === tab.id;
            const itemClasses = [
              "sidebar-tab-panel__tab",
              isActive && "sidebar-tab-panel__tab--active",
              tab.disabled && "sidebar-tab-panel__tab--disabled",
            ]
              .filter(Boolean)
              .join(" ");

            return (
              <button
                key={tab.id}
                type="button"
                className={itemClasses}
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                disabled={tab.disabled}
                onClick={() => selectTab(tab)}
                onKeyDown={(event) => handleKeyDown(event, tab, index)}
              >
                <span className="sidebar-tab-panel__label" title={tab.label}>
                  {tab.label}
                </span>
                {tab.icon && (
                  <span className="sidebar-tab-panel__icon" aria-hidden>
                    {tab.icon}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      <div className="sidebar-tab-panel__content" role="tabpanel">
        {content}
      </div>
    </div>
  );
};
